/**
 * Shareable map state in the URL HASH (e.g. `#map=61.4978/23.7610/9.5/recent,percapita`):
 * viewport plus active layer toggles, so a map view can be linked/shared. The hash, not query
 * params, because the SRP's search-state URL sync owns the query string (unknown params there
 * risk being parsed as refinements or dropped on rewrite), while the hash passes through both
 * SSR routing and the search sync untouched.
 *
 * Deliberately EXCLUDED: the commute origin. It is often the user's home; putting it in the URL
 * would leak it into browser history and analytics pageview URLs. Sharing a commute area stays
 * an explicit choice, not a side effect.
 *
 * Phase A (map-mode-in-hash) extends the grammar without breaking the shape above:
 * `#map[=lat/lng/zoom[/layers]][&job=<id>][&area=view]`. A bare `#map` (no `=...`) still means
 * "map mode, default camera for this page." Presence of a `#map` (with or without `=...`) is
 * itself the map-mode flag; its absence means list mode. See `parseSrpMapHash`/`buildSrpMapHash`
 * below, which wrap the camera grammar above and add `job`/`area`.
 */

export interface MapUrlHashState {
  center: { lat: number; lng: number };
  zoom: number;
  recentOnly: boolean;
  perCapita: boolean;
}

const HASH_PREFIX = '#map=';

export function buildMapUrlHash(state: MapUrlHashState): string {
  const layers = [state.recentOnly ? 'recent' : null, state.perCapita ? 'percapita' : null]
    .filter(Boolean)
    .join(',');
  const parts = [state.center.lat.toFixed(4), state.center.lng.toFixed(4), state.zoom.toFixed(2)];
  if (layers) parts.push(layers);
  return `${HASH_PREFIX}${parts.join('/')}`;
}

export function parseMapUrlHash(hash: string | null | undefined): MapUrlHashState | null {
  if (!hash || !hash.startsWith(HASH_PREFIX)) return null;
  const parts = hash.slice(HASH_PREFIX.length).split('/');
  if (parts.length < 3) return null;
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  const zoom = Number(parts[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) return null;
  // Sanity-bound to the map's own reachable space (Finland bbox + padding, plausible zooms) —
  // a mangled shared link falls back to the default view instead of a broken camera.
  if (lat < 55 || lat > 73 || lng < 15 || lng > 36 || zoom < 3 || zoom > 22) return null;
  const layers = new Set((parts[3] || '').split(',').filter(Boolean));
  return {
    center: { lat, lng },
    zoom,
    recentOnly: layers.has('recent'),
    perCapita: layers.has('percapita'),
  };
}

/**
 * Phase B1 "lens": what the region-shading + insight line are computed from. `volume` (open-job
 * density, the pre-existing behaviour) is the default and is deliberately never written into the
 * hash, so every pre-B1 shared link still parses as `lens: 'volume'` with zero grammar changes.
 */
export type MapLens = 'volume' | 'work' | 'field';

const MAP_LENS_VALUES: readonly MapLens[] = ['volume', 'work', 'field'];

function isMapLens(value: string): value is MapLens {
  return (MAP_LENS_VALUES as readonly string[]).includes(value);
}

/**
 * Full Phase A map-mode hash state: whether the hash puts the SRP in map mode at all, plus the
 * optional camera (from the grammar above), the open job preview id, and whether the results
 * list should follow the viewport ("area=view") rather than stay pinned to the page's own place.
 *
 * Phase B1 adds `lens` (see `MapLens`); an unrecognized `lens=` value falls back to `volume`
 * rather than rejecting the whole hash, same graceful-degradation policy as the trailing-segment
 * handling below.
 */
export interface SrpMapHashState {
  isMapMode: boolean;
  camera: MapUrlHashState | null;
  jobId: string | null;
  followViewport: boolean;
  lens: MapLens;
}

const EMPTY_MAP_HASH_STATE: SrpMapHashState = {
  isMapMode: false,
  camera: null,
  jobId: null,
  followViewport: false,
  lens: 'volume',
};

/**
 * Cheap, allocation-light check for "is this a map-mode hash": used before paint (inline head
 * script) and in the URL-rewrite 301 handler, where we don't need the full parsed state.
 */
export function isMapModeHash(hash: string | null | undefined): boolean {
  if (!hash) return false;
  return hash === '#map' || hash.startsWith('#map=') || hash.startsWith('#map&');
}

/**
 * Parses the full Phase A hash grammar: `#map[=lat/lng/zoom[/layers]][&job=<id>][&area=view]`.
 * A bare `#map` (no `=...`) is valid map mode with no explicit camera (caller falls back to the
 * page's own default view). Unknown trailing segments are ignored rather than rejected, so a
 * future addition to the grammar degrades gracefully on older shared links.
 */
export function parseSrpMapHash(hash: string | null | undefined): SrpMapHashState {
  if (!isMapModeHash(hash)) return EMPTY_MAP_HASH_STATE;
  const raw = hash as string;
  const segments = raw.slice(1).split('&'); // drop leading '#'; segments[0] is 'map' or 'map=...'
  const mapSegment = segments[0];
  const camera = mapSegment.startsWith('map=') ? parseMapUrlHash(`#${mapSegment}`) : null;

  let jobId: string | null = null;
  let followViewport = false;
  let lens: MapLens = 'volume';
  for (const segment of segments.slice(1)) {
    const eq = segment.indexOf('=');
    if (eq === -1) continue;
    const key = segment.slice(0, eq);
    const value = decodeURIComponent(segment.slice(eq + 1));
    if (key === 'job' && value) jobId = value;
    if (key === 'area' && value === 'view') followViewport = true;
    if (key === 'lens' && isMapLens(value)) lens = value;
  }

  return { isMapMode: true, camera, jobId, followViewport, lens };
}

/**
 * Builds the full Phase A(+B1) hash from its parts. `camera: null` produces a bare `#map` (or
 * `#map&job=...`); omit `jobId`/`followViewport`/`lens` (or pass falsy/`'volume'`) to leave those
 * segments out. `lens` is only ever written when it isn't the default, matching the `recent`-
 * /`percapita`-style "only present when active" convention the camera grammar already uses.
 */
export function buildSrpMapHash(state: {
  camera: MapUrlHashState | null;
  jobId?: string | null;
  followViewport?: boolean;
  lens?: MapLens;
}): string {
  const base = state.camera ? buildMapUrlHash(state.camera) : '#map';
  const extra: string[] = [];
  if (state.jobId) extra.push(`job=${encodeURIComponent(state.jobId)}`);
  if (state.followViewport) extra.push('area=view');
  if (state.lens && state.lens !== 'volume') extra.push(`lens=${state.lens}`);
  return extra.length ? `${base}&${extra.join('&')}` : base;
}
